// DISC profile display. Reads the four dimension scores (D/I/S/C)
// from the stored talent_profile_scores object and renders the
// dominant type pill + a 4-bar chart. Hides itself if no DISC data
// is present (staff who took the assessment before migration 042
// added DISC questions don't have scores yet).

const DISC_META = {
  D: { name: 'Dominance',         color: 'bg-red-500',    pill: 'bg-red-100 text-red-800 border-red-300' },
  I: { name: 'Influence',         color: 'bg-yellow-500', pill: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  S: { name: 'Steadiness',        color: 'bg-green-500',  pill: 'bg-green-100 text-green-800 border-green-300' },
  C: { name: 'Conscientiousness', color: 'bg-blue-500',   pill: 'bg-blue-100 text-blue-800 border-blue-300' },
};

// One-line blurb per DISC type. Pure types + the 12 two-letter blends.
const DISC_BLURBS = {
  D:  'Direct and decisive — drives for results and quick decisions.',
  I:  'Outgoing and persuasive — builds connections and inspires others.',
  S:  'Steady and supportive — values consistency and team harmony.',
  C:  'Analytical and precise — focused on accuracy and quality.',
  DI: 'Driver-Influencer — bold, persuasive, and quick to act.',
  DC: 'Driver-Analyzer — results-focused with attention to quality.',
  DS: 'Driver-Steady — decisive but patient with people.',
  ID: 'Influencer-Driver — enthusiastic with a strong push to win.',
  IS: 'Influencer-Steady — warm, supportive, and people-oriented.',
  IC: 'Influencer-Analyzer — persuasive yet careful with details.',
  SD: 'Steady-Driver — calm, dependable, and goal-aware.',
  SI: 'Steady-Influencer — patient connector and team-builder.',
  SC: 'Steady-Analyzer — calm, methodical, and dependable.',
  CD: 'Analyzer-Driver — careful and demanding of excellence.',
  CI: 'Analyzer-Influencer — precise and personable.',
  CS: 'Analyzer-Steady — thorough, reliable, and consistent.',
};

// Compute dominant + secondary from a scores object that includes
// D/I/S/C keys. Returns null when no DISC data was captured.
export function computeDisc(scores) {
  if (!scores) return null;
  const d = Number(scores.D) || 0;
  const i = Number(scores.I) || 0;
  const s = Number(scores.S) || 0;
  const c = Number(scores.C) || 0;
  // Sum of |x| catches the "answered, but balanced" case while still
  // letting "didn't answer DISC at all" be null.
  if (Math.abs(d) + Math.abs(i) + Math.abs(s) + Math.abs(c) === 0) return null;
  const dims = [['D', d], ['I', i], ['S', s], ['C', c]].sort((a, b) => b[1] - a[1]);
  const [primary, primaryScore] = dims[0];
  const [secondary, secondaryScore] = dims[1];
  // Secondary if within 75% of the dominant — the standard DISC
  // "blend" threshold. Otherwise show the pure dominant.
  const showSecondary =
    secondaryScore > 0 && primaryScore > 0 &&
    secondaryScore >= primaryScore * 0.75;
  return {
    scores: { D: d, I: i, S: s, C: c },
    primary,
    secondary: showSecondary ? secondary : null,
    type: showSecondary ? `${primary}${secondary}` : primary,
  };
}

export default function DiscProfile({ scores, compact = false }) {
  const disc = computeDisc(scores);
  if (!disc) return null;
  const primaryMeta = DISC_META[disc.primary];
  const blurb = DISC_BLURBS[disc.type] || DISC_BLURBS[disc.primary];

  if (compact) {
    return (
      <span title={`DISC: ${disc.type} — ${blurb}`}
        className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-0.5 rounded-full border ${primaryMeta.pill}`}>
        DISC: {disc.type}
      </span>
    );
  }

  // Largest absolute score sets the chart scale.
  const maxAbs = Math.max(...['D', 'I', 'S', 'C'].map(k => Math.abs(disc.scores[k]))) || 1;

  return (
    <div className="grid md:grid-cols-[1fr_2fr] gap-6">
      <div>
        <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">DISC Type</div>
        <div className="flex items-baseline gap-2">
          <div className="text-3xl font-bold text-gray-900">{disc.type}</div>
          <div className="text-sm text-gray-500">
            {DISC_META[disc.primary].name}
            {disc.secondary && ` + ${DISC_META[disc.secondary].name}`}
          </div>
        </div>
        <p className="text-sm text-gray-600 mt-2">{blurb}</p>
      </div>
      <div className="space-y-2">
        <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Dimension scores</div>
        {['D', 'I', 'S', 'C'].map(dim => {
          const score = disc.scores[dim];
          // Center at 0; negative goes left, positive goes right.
          const pctOfMax = (Math.abs(score) / maxAbs) * 50;
          const isDominant = dim === disc.primary;
          return (
            <div key={dim}>
              <div className="flex items-center justify-between text-xs mb-0.5">
                <span className={`font-medium ${isDominant ? 'text-gray-900 font-bold' : 'text-gray-700'}`}>
                  <span className="inline-block w-4">{dim}</span> {DISC_META[dim].name}
                </span>
                <span className="text-gray-500">{score > 0 ? `+${score}` : score}</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden relative">
                <div className="absolute top-0 bottom-0 left-1/2 w-px bg-gray-300" />
                {score >= 0 ? (
                  <div className={`absolute top-0 bottom-0 left-1/2 ${DISC_META[dim].color}`}
                    style={{ width: `${pctOfMax}%` }} />
                ) : (
                  <div className={`absolute top-0 bottom-0 ${DISC_META[dim].color}`}
                    style={{ left: `${50 - pctOfMax}%`, width: `${pctOfMax}%` }} />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
